import { findFirstNonEmptyString, notEmpty } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { query } from "~/components/article/article-footer/morning-studio-article-footer";
import { ContentItemRender } from "~/components/content/content-item-render";
import { checkIsMainTopic } from "~/components/content/helpers";
import { EntityLink } from "~/components/entity-link";
import type { morningStudioArticleFooterDisplayQuery$key } from "~/queries/__generated__/morningStudioArticleFooterDisplayQuery.graphql";
import type { morningStudioArticleFooterQuery } from "~/queries/__generated__/morningStudioArticleFooterQuery.graphql";

import {
  ArticlePlaceholder,
  ArticlesContainer,
  Container,
  TitleContainer,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  queryReference: PreloadedQuery<morningStudioArticleFooterQuery>;
};

export const MorningStudioArticleFooterDisplay: FunctionComponent<Props> = ({
  className,
  queryReference,
}) => {
  const queries = usePreloadedQuery<morningStudioArticleFooterQuery>(query, queryReference);
  const data = useFragment<morningStudioArticleFooterDisplayQuery$key>(
    graphql`
      fragment morningStudioArticleFooterDisplayQuery on Query
      @argumentDefinitions(
        applicationId: { type: "String!" }
        articleTypeId: { type: "String!" }
        entityUuid: { type: "String!" }
        topicEntityUuid: { type: "String" }
        withTopic: { type: "Boolean!" }
      ) {
        article(filter: { entityUuid: $entityUuid }) {
          entityId
          topics {
            entityId
            sponsor {
              name
            }
          }
          ...helpersCheckIsMainTopicContent
          seriesContainer {
            socialHeadline
            headline
            ...entityLink
            series {
              ... on Content {
                entityId
                ...contentItemRenderContent
              }
            }
          }
        }
        topic(filter: { entityUuid: $topicEntityUuid }) @include(if: $withTopic) {
          ...entityLink
          name
          contents(
            first: 10
            filter: { applicationIds: [$applicationId] }
            exclude: { articleTypeIds: [$articleTypeId] }
          ) {
            edges {
              node {
                entityId
                ...contentItemRenderContent
              }
            }
          }
        }
      }
    `,
    queries,
  );

  const mainTopic = useMemo(
    () => data.article.topics?.find(topic => checkIsMainTopic(data.article, topic?.entityId)),
    [data.article],
  );

  const contentList = useMemo(
    () =>
      [
        ...(data.topic?.contents?.edges?.map(edge => edge.node) ?? []),
        ...(data.article.seriesContainer?.series ?? []),
      ]
        .filter(notEmpty)
        .filter(edge => edge.entityId !== data.article.entityId),
    [data.article.entityId, data.article.seriesContainer?.series, data.topic?.contents?.edges],
  );

  const handleRenderTitle = () => {
    const sectionName = findFirstNonEmptyString(
      mainTopic?.sponsor?.name,
      data.topic?.name,
      data.article?.seriesContainer?.socialHeadline,
      data.article?.seriesContainer?.headline,
    );
    const entityLink = data.topic ?? data.article.seriesContainer;
    if (!entityLink) return <>Others from {sectionName}</>;
    return <EntityLink reference={entityLink}>Others from {sectionName}</EntityLink>;
  };

  const handleRenderArticles = () =>
    contentList.map((node, index) => (
      <ContentItemRender key={index} reference={node} variant="morning-studio" />
    ));

  if (contentList.length === 0) return null;

  return (
    <Container className={className}>
      <Wrapper>
        <TitleContainer>{handleRenderTitle()}</TitleContainer>
        <ArticlesContainer>
          {handleRenderArticles()}
          {contentList.length % 2 === 1 && <ArticlePlaceholder />}
        </ArticlesContainer>
      </Wrapper>
    </Container>
  );
};

MorningStudioArticleFooterDisplay.displayName = "MorningStudioArticleFooterDisplay";
